import { RadioOption } from '@app/components/FormControl/FormRadioButton/FormRadioButton';
import { NAVIGATE_ROUTE } from '@app/modules/routes/common-route';
import { BV002MenuItem } from '@app/pages/private/bv002/types';
import addCmtIcon from '@src/assets/imgs/icons/add-comment--solid-gray.svg';
import appsIcon from '@src/assets/imgs/icons/apps-solid-gray.svg';
import changeCatalogIcon from '@src/assets/imgs/icons/change-catalog-solid-gray.svg';
import layerIcon from '@src/assets/imgs/icons/layer-solid-gray.svg';
import QAIcon from '@src/assets/imgs/icons/Q&A-solid-gray.svg';
import RunIcon from '@src/assets/imgs/icons/run-solid-gray.svg';
import i18n from 'i18next';

export const SYSTEM_APP = 'aps-poc';
export const getSystemName = () => i18n.t('title');

/**
 * constant definitions are applied over application
 */
export const EMPTY_OBJECT = {};
export const EMPTY_ARRAY = [];
export const EMPTY = '';
export const DASH_FULL_SIZE = 'ー';
export const DASH_HALF_SIZE = '-';
export const SLASH = '/';
export const COLON = ':';
export const HYPHEN = '-';
export const SECONDS_PER_MINUTE = 60;
export const MILLISECONDS_PER_SECOND = 1000;
export const NINE = 9;
export const ONE_SPACE = ' ';
export const COMMA = ',';
export const DOT = '.';
export const MINUS = '-';
export const DIGITS = '0123456789'.split(EMPTY);
export const ALLOWED_NUMBER_CHARACTERS = [...DIGITS, COMMA, DOT, MINUS];

export const PAD_TWO = 2;
export const PAD_FOUR = 4;
export const ZERO = '0';
export const MAX_COMMENT_LENGTH = 2000;
export const MAX_THREAD_NAME_LENGTH = 200;
export const MAX_REQUEST_NAME_LENGTH = 50; // max length for request name (依頼名)

/**
 * Cache duration for user info in IndexedDB (60 minutes)
 * 60 minutes * 60 seconds * 1000 milliseconds = 3,600,000 ms
 */
export const USER_INFO_CACHE_DURATION = 120 * 60 * 1000;

/**
 * Threshold to refresh cache before expiration (10 minutes)
 * Only refresh cache if remaining time is less than this threshold
 */
export const CACHE_REFRESH_THRESHOLD = 10 * 60 * 1000;

/**
 * list of common keys we will use in indexdb table
 */
export enum INDEXDB_COMMON_KEYS {
  ERROR_LIST = '__common_error_list__',
  USER_INFO = '__common_user_info__',
  LANGUAGE_PREFERENCE = '__common_language_preference__',
}

/**
 * list of screenIds
 */
export enum SCREEN_IDS {
  SC001 = 'SC001',
  SC002 = 'SC002',
  SC003 = 'SC003',
  SC004 = 'SC004',
  SC005 = 'SC005',
  SC006 = 'SC006',
  SC007 = 'SC007',
  SC008 = 'SC008',
  SC009 = 'SC009',
  SC010 = 'SC010',
  SC011 = 'SC011',
  SC012 = 'SC012',
  SC013 = 'SC013',
  SC014 = 'SC014',
  BV001 = 'BV001',
  BV002 = 'BV002',
  BV002_D001 = 'BV002-D001',
  BV002_D002 = 'BV002-D002',
  BV002_D003 = 'BV002-D003',
  BV002_D004 = 'BV002-D004',
  BV002_D005 = 'BV002-D005',
  BV002_D006 = 'BV002-D006',
  BV002_D007 = 'BV002-D007',
}

/**
 * list of functionIds
 */
export const FUNC_IDS = {
  [SCREEN_IDS.SC002]: {
    START_SHARING: 'START_SHARING',
  },
  [SCREEN_IDS.SC003]: {
    CHECK_SHARING_STATUS: 'CHECK_SHARING_STATUS',
  },
  [SCREEN_IDS.SC004]: {
    SELECT_PROJECT: 'SELECT_PROJECT',
    SELECT_REQUEST: 'SELECT_REQUEST',
  },
  [SCREEN_IDS.SC005]: {
    ADD_REQUEST: 'ADD_REQUEST',
    VIEW_REQUEST: 'VIEW_REQUEST',
    UPDATE_REQUEST: 'UPDATE_REQUEST',
    DELETE_REQUEST: 'DELETE_REQUEST',
  },
  [SCREEN_IDS.SC006]: {
    CHANGE_DATASET_NAME: 'CHANGE_DATASET_NAME',
    DELETE_DATASET: 'DELETE_DATASET',
    CHECK_SHARING_STATUS: 'CHECK_SHARING_STATUS',
    FILE_MANAGEMENT: 'FILE_MANAGEMENT',
    VIEW_FILES: 'VIEW_FILES',
    DOWNLOAD_DATASET: 'DOWNLOAD_DATASET',
  },
  [SCREEN_IDS.SC007]: {
    IMPORT_DOCUMENT: 'IMPORT_DOCUMENT',
    EDIT_DOCUMENT: 'EDIT_DOCUMENT',
    DELETE_DOCUMENT: 'DELETE_DOCUMENT',
    VIEW_BOX_DATA: 'VIEW_BOX_DATA',
    CHECK_AUTO_CHECK_RESULTS: 'CHECK_AUTO_CHECK_RESULTS',
    DOWNLOAD_DOCUMENT: 'DOWNLOAD_DOCUMENT',
  },
  [SCREEN_IDS.SC008]: {
    ADD_PROJECT_MEMBER: 'ADD_PROJECT_MEMBER',
    REMOVE_MEMBER: 'REMOVE_MEMBER',
    UPDATE_ROLE: 'UPDATE_ROLE',
    UPSERT_MEMBER: 'UPSERT_MEMBER',
  },
  [SCREEN_IDS.SC009]: {
    VIEW_PROJECT: 'VIEW_PROJECT',
  },
  [SCREEN_IDS.SC010]: {
    SET_REQUEST: 'SET_REQUEST_DESTINATION',
  },
  [SCREEN_IDS.SC011]: {
    VIEW_PROJECT: 'VIEW_PROJECT',
    ADD_PROJECT_MEMBER: 'ADD_PROJECT_MEMBER',
  },
  [SCREEN_IDS.SC012]: {
    USE_MODEL_VIEWER: 'USE_MODEL_VIEWER',
    POST_TO_CHAT: 'POST_TO_CHAT',
    DOWNLOAD_DATASET: 'DOWNLOAD_DATASET',
    UPDATE_REQUEST: 'UPDATE_REQUEST',
    DELETE_REQUEST: 'DELETE_REQUEST',
    UPDATE_DEADLINE_PROGRESS: 'UPDATE_DEADLINE_PROGRESS',
    TASK_REQUEST: 'TASK_REQUEST',
    LINK_TO_DLM_PLATFORM: 'LINK_TO_DLM_PLATFORM',
    SHARE_ESTIMATION_RESULTS: 'SHARE_ESTIMATION_RESULTS',
  },
  [SCREEN_IDS.SC013]: {
    ADD_USER: 'ADD_USER',
    EDIT_USER: 'EDIT_USER',
    SET_USER_ROLE: 'SET_USER_ROLE',
    DISABLE_ACCOUNT: 'DISABLE_ACCOUNT',
    DELETE_USER: 'DELETE_USER',
  },
  [SCREEN_IDS.BV001]: {
    SELECT_DATA: 'SELECT_DATA',
    OPEN_MODEL_VIEWER: 'OPEN_MODEL_VIEWER',
    AUTOMATIC_CHECK_RESULTS: 'AUTOMATIC_CHECK_RESULTS',
    MANAGEMENT_TOPIC: 'MANAGEMENT_TOPIC',
    CHANGE_HISTORY: 'CHANGE_HISTORY',
    DOWNLOAD_DOCUMENT: 'DOWNLOAD_DOCUMENT',
  },
  [SCREEN_IDS.BV002]: {
    OPERATE_MODEL: 'OPERATE_MODEL',
  },
  [SCREEN_IDS.BV002_D001]: {
    CONFIRM_SINGLE_COMPONENT_SELECTION: 'CONFIRM_SINGLE_COMPONENT_SELECTION',
    EDIT_COMPONENT_INFORMATION: 'EDIT_COMPONENT_INFORMATION',
  },
  [SCREEN_IDS.BV002_D002]: {
    CONFIRM_SAME_COMPONENT_INFORMATION: 'CONFIRM_SAME_COMPONENT_INFORMATION',
    MERGE_SAME_COMPONENT_INFORMATION: 'MERGE_SAME_COMPONENT_INFORMATION',
  },
  [SCREEN_IDS.BV002_D003]: {
    CREATE_NEW_TOPIC: 'CREATE_NEW_TOPIC',
  },
  [SCREEN_IDS.BV002_D004]: {
    SELECT_TOPIC: 'SELECT_TOPIC',
  },
  [SCREEN_IDS.BV002_D005]: {
    CHECK_TOPIC_ATTACHED_FILES: 'CHECK_TOPIC_ATTACHED_FILES',
    DOWNLOAD_DOCUMENT: 'DOWNLOAD_DOCUMENT',
  },
  [SCREEN_IDS.BV002_D006]: {
    OPERATE_MODEL: 'OPERATE_MODEL',
  },
  [SCREEN_IDS.BV002_D007]: {
    CHECK_SHARING_STATUS: 'AUTOMATION_CHECK',
  },
  "COMMON": {
    CHECK_SHARING_STATUS: 'CHECK_SHARING_STATUS',
  }
} as const;

/**
 * list of paginator options
 */
export const PAGINATOR_OPTIONS = [20, 50, 100];
export const MAX_PAGINATOR_ITEMS = 5;
export const MAX_PAGINATOR_DATASET_ITEMS = 2;

export const MIN_DATE = new Date(1990, 0, 1);
export const MAX_DATE = new Date(2050, 11, 31);

/**
 * File type constants for document icons
 */
export enum FILE_TYPE {
  // Spreadsheet
  XLS = 'xls',
  XLSX = 'xlsx',
  XLSM = 'xlsm',
  CSV = 'csv',
  // Presentation
  PPT = 'ppt',
  PPTX = 'pptx',
  GSLIDE = 'gslide',
  GSLIDES = 'gslides',
  // Image
  JPEG = 'jpeg',
  JPG = 'jpg',
  PNG = 'png',
  RAW = 'raw',
  SVG = 'svg',
  // Document
  PDF = 'pdf',
  DOC = 'doc',
  DOCX = 'docx',
  GDOC = 'gdoc',
  // Data
  JSON = 'json',
  // CAD / BIM
  IFC = 'ifc',
  DWG = 'dwg',
  PLA = 'pla',
  RVT = 'rvt',
  THREEDM = '3dm',
  SKP = 'skp',
  STB = 'stb',
  // Fallback (keep for backward compat)
  EXCEL = 'excel',
  OTHER = 'other',
}

export enum SUB_TASK_TYPE {
  TAK_QS = 'takqs',
  ESTIMATE = 'estimate',
}
export const ALLOWED_EXTENSIONS = new Set<string>([
  FILE_TYPE.XLS,
  FILE_TYPE.XLSX,
  FILE_TYPE.XLSM,
  FILE_TYPE.CSV,
  FILE_TYPE.PPT,
  FILE_TYPE.PPTX,
  FILE_TYPE.GSLIDE,
  FILE_TYPE.GSLIDES,
  FILE_TYPE.JPEG,
  FILE_TYPE.JPG,
  FILE_TYPE.PNG,
  FILE_TYPE.RAW,
  FILE_TYPE.SVG,
  FILE_TYPE.PDF,
  FILE_TYPE.DOC,
  FILE_TYPE.DOCX,
  FILE_TYPE.GDOC,
  FILE_TYPE.IFC,
  FILE_TYPE.DWG,
]);

export const ALLOWED_EXTENSIONS_SC012 = new Set<string>([
  FILE_TYPE.XLS,
  FILE_TYPE.XLSX,
  FILE_TYPE.XLSM,
  FILE_TYPE.CSV,
  FILE_TYPE.PPT,
  FILE_TYPE.PPTX,
  FILE_TYPE.GSLIDE,
  FILE_TYPE.GSLIDES,
  FILE_TYPE.JPEG,
  FILE_TYPE.JPG,
  FILE_TYPE.PNG,
  FILE_TYPE.RAW,
  FILE_TYPE.SVG,
  FILE_TYPE.PDF,
  FILE_TYPE.DOC,
  FILE_TYPE.DOCX,
  FILE_TYPE.GDOC,
  FILE_TYPE.IFC,
  FILE_TYPE.DWG,
  FILE_TYPE.PLA,
  FILE_TYPE.RVT,
  FILE_TYPE.THREEDM,
  FILE_TYPE.SKP,
  FILE_TYPE.STB,
]);

export const MAX_TOTAL_SIZE = 50 * 1024 * 1024; // 50 MB
export const MAX_TOTAL_SIZE_LABEL = '50MB';
export const FILE_EXPORT_TYPE = {
  EXCEL: 'EXCEL',
  CSV: 'CSV',
  PDF: 'PDF',
} as const;

// Invalid file name characters: < > : " / \ | ? *
export const INVALID_FILE_NAME_REGEX = /[<>:"/\\|?*]/g;

export enum SORT_ORDER {
  ASC = 'ASC',
  DESC = 'DESC',
}

export enum DATASET_STATUS {
  NOT_STARTED = '001',
  IN_PROGRESS = '002',
  DONE = '003',
  OVERDUE = '004',
}

export const PHASE_CODE = '002';

export const ROUTE_REDIRECT = {
  SC003: (publicationGroupId: string, projectId: number, publicationGuid?: string) =>
    `${NAVIGATE_ROUTE.SC003}?projectId=${projectId}&publicationGroupId=${publicationGroupId}&publicationGuid=${publicationGuid}`,
} as const;

export const ENVIRONMENTS = {
  LOCAL_DEV: 'LOCAL_DEV',
  DEV: 'DEV',
  STAGING: 'STAGING',
  PRODUCTION: 'PRODUCTION',
} as const;

export const USER_ACTION = {
  USER_DELETE: 'USER_DELETE', // ユーザー削除時
  ACCOUNT_DEACTIVATE: 'ACCOUNT_DEACTIVATE', // アカウント無効化時
} as const;

export const LOGIN_LOCAL_FOR_SCREEN = [ENVIRONMENTS.LOCAL_DEV, ENVIRONMENTS.DEV] as const;

export const ADMIN_PERMISSION = '*';

export const getMenuItems = (t: any): BV002MenuItem[] => [
  {
    label: t('bv002Menu.model'),
    icon: 'pi pi-box',
    key: 'btnModel',
    titleDialog: 'bv002Menu.model',
    action: null,
  },
  {
    label: t('bv002Menu.memberInfo'),
    iconImg: layerIcon,
    action: null,
    items: [
      {
        label: t('bv002Menu.estimateInfo'),
        icon: 'pi pi-calculator',
        key: 'btnCostEstimate',
        titleDialog: 'bv002Menu.estimateInfo',
        action: null,
      },
      {
        label: t('bv002Menu.identicalMemberInfo'),
        icon: 'pi pi-clone',
        key: 'btnDuplicateComponent',
        titleDialog: 'bv002Menu.identicalMemberInfo',
        action: null,
      },
      {
        label: t('bv002Menu.memberChangeHistory'),
        icon: 'pi pi-history',
        key: 'btnComponentHistory',
        titleDialog: 'bv002Menu.memberInfo',
        action: null,
        requiredSelectedIds: true,
      },
    ],
  },
  {
    label: t('bv002Menu.newTopic'),
    iconImg: addCmtIcon,
    key: 'btnNewTopic',
    titleDialog: 'bv002Menu.newTopicTitle',
    action: null,
    requiredSelectedIds: true,
  },
  {
    label: t('bv002Menu.qaManagement'),
    iconImg: QAIcon,
    key: 'btnQaManagement',
    titleDialog: 'bv002Menu.qaManagement',
    action: null,
  },
  {
    label: t('bv002Menu.topicFileManagement'),
    iconImg: appsIcon,
    key: 'btnDocumentManagement',
    titleDialog: 'bv002Menu.topicFileManagement',
    action: null,
  },
  {
    label: t('bv002Menu.changeHistory'),
    iconImg: changeCatalogIcon,
    key: 'btnChangeHistory',
    titleDialog: 'bv002Menu.changeHistory',
    action: null,
  },
  {
    label: t('bv002Menu.autoCheckResult'),
    iconImg: RunIcon,
    key: 'btnAutomaticCheckResults',
    titleDialog: 'bv002Menu.autoCheckResult',
    action: null,
  },
];

export const typeReport: RadioOption[] = [
  { label: 'PDF', value: 'PDF' },
  { label: 'Excel', value: 'EXCEL' },
  { label: 'CSV', value: 'CSV' },
];

export const allowedOpenDialogList = ['D001', 'D002', 'D003', 'D004', 'D005', 'D007'];
